import { API_CACHE_PREFIX } from '@/constants/appIdentity'

const API_CACHE_INDEX_KEY = `${API_CACHE_PREFIX}index`

export const DEFAULT_API_CACHE_TTL = 60 * 1000
export const STATIC_API_CACHE_TTL = 10 * 60 * 1000
export const VOLATILE_API_CACHE_TTL = 20 * 1000

/** 配置类 / 公开列表：缓存更久 */
export const STATIC_CACHE_PATTERNS = [
  /\/api\/v1\/webViewConfig\/?$/,
  /\/api\/v1\/notification\/?$/,
  /\/api\/v1\/lottery\/prizes\/?$/,
  /\/api\/v1\/mall\/products\/?$/,
]

/** 账户 / 余额 / 订单 / 状态：短缓存 */
export const VOLATILE_CACHE_PATTERNS = [
  /\/user\/usersProfile\/?$/,
  /\/user\/check-in\/?$/,
  /\/user\/fundFlowList\/?$/,
  /\/user\/bankCard\/?$/,
  /\/user\/withdraw\/?$/,
  /\/user\/addresses\/?$/,
  /\/user\/referral\/reward\/list\/?$/,
  /\/user\/detail\/[^/]+\/getPromotions\/?$/,
  /\/api\/v1\/mall\/orders\/?$/,
  /\/api\/v1\/lottery\/records\/?$/,
]

/** 有副作用或不该缓存的 GET */
export const NO_CACHE_PATTERNS = [
  /\/api\/v1\/app-download-log\/?$/,
]

function getCacheStorage() {
  try {
    return sessionStorage
  } catch (e) {
    return null
  }
}

function stableStringify(value) {
  if (value == null) return ''
  if (typeof value !== 'object') return String(value)
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`
  }
  const keys = Object.keys(value).sort()
  return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`
}

function hashString(input) {
  const str = String(input || '')
  let hash = 0
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash |= 0
  }
  return `h${Math.abs(hash)}`
}

function readIndex(storage) {
  try {
    const raw = storage.getItem(API_CACHE_INDEX_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

function writeIndex(storage, keys) {
  try {
    storage.setItem(API_CACHE_INDEX_KEY, JSON.stringify(keys))
  } catch (e) {
    // ignore quota errors
  }
}

function trackCacheKey(storage, cacheKey) {
  const index = readIndex(storage)
  if (!index.includes(cacheKey)) {
    index.push(cacheKey)
    writeIndex(storage, index)
  }
}

function untrackCacheKey(storage, cacheKey) {
  const index = readIndex(storage).filter((key) => key !== cacheKey)
  writeIndex(storage, index)
}

export function getRequestUrl(config) {
  const raw = config.url || ''
  try {
    if (/^https?:\/\//i.test(raw)) {
      return new URL(raw).pathname
    }
    const base = config.baseURL || ''
    if (base && /^https?:\/\//i.test(base)) {
      return new URL(raw, base).pathname
    }
  } catch (e) {
    // fall through
  }
  return raw.split('?')[0]
}

export function isCacheableRequest(config) {
  if (!config || config.skipApiCache || config.force) return false
  const method = (config.method || 'get').toLowerCase()
  if (method !== 'get') return false
  const url = getRequestUrl(config)
  return !NO_CACHE_PATTERNS.some((pattern) => pattern.test(url))
}

export function getCacheTTL(config) {
  if (typeof config.cacheTTL === 'number') return config.cacheTTL
  const url = getRequestUrl(config)
  if (STATIC_CACHE_PATTERNS.some((pattern) => pattern.test(url))) {
    return STATIC_API_CACHE_TTL
  }
  if (VOLATILE_CACHE_PATTERNS.some((pattern) => pattern.test(url))) {
    return VOLATILE_API_CACHE_TTL
  }
  return DEFAULT_API_CACHE_TTL
}

export function getCacheKey(config, token) {
  if (config.cacheKey) {
    return `${API_CACHE_PREFIX}${config.cacheKey}`
  }

  const baseURL = config.baseURL || process.env.VUE_APP_BASE_API || ''
  const url = getRequestUrl(config)
  const params = stableStringify(config.params || {})
  const scope = token ? `u:${hashString(token)}` : 'public'

  return `${API_CACHE_PREFIX}${scope}:${baseURL}:${url}:${params}`
}

export function readApiCache(cacheKey, allowStale = false) {
  const storage = getCacheStorage()
  if (!storage || !cacheKey) return null

  try {
    const raw = storage.getItem(cacheKey)
    if (raw == null) return null

    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !('data' in parsed)) {
      return null
    }

    const expired = typeof parsed.expiresAt === 'number' && parsed.expiresAt <= Date.now()
    if (expired && !allowStale) return null
    return parsed.data
  } catch (e) {
    return null
  }
}

export function writeApiCache(cacheKey, data, ttl) {
  const storage = getCacheStorage()
  if (!storage || !cacheKey || typeof ttl !== 'number' || ttl <= 0) return

  const entry = {
    data,
    expiresAt: Date.now() + ttl,
  }

  try {
    storage.setItem(cacheKey, JSON.stringify(entry))
    trackCacheKey(storage, cacheKey)
  } catch (e) {
    // storage full: drop oldest tracked keys then retry once
    try {
      const index = readIndex(storage)
      index.slice(0, Math.min(5, index.length)).forEach((key) => {
        storage.removeItem(key)
        untrackCacheKey(storage, key)
      })
      storage.setItem(cacheKey, JSON.stringify(entry))
      trackCacheKey(storage, cacheKey)
    } catch (err) {
      // ignore
    }
  }
}

export function clearApiCache() {
  const storage = getCacheStorage()
  if (!storage) return

  try {
    const index = readIndex(storage)
    index.forEach((key) => storage.removeItem(key))
    storage.removeItem(API_CACHE_INDEX_KEY)

    // 兜底清理遗留键
    const keysToRemove = []
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i)
      if (key && key.startsWith(API_CACHE_PREFIX)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => storage.removeItem(key))
  } catch (e) {
    // ignore
  }
}
